/** The title screen.
 *
 * One painted key-art frame (`ui.menu.title_key_art`, a 1920x1080 painting
 * generated for this screen and stored in the UI library), a chiselled gold
 * logo raster (`ui.menu.title_logo`) in the upper left, and a column of gilt
 * relief plaques down the right carrying a painted device and a stamped label.
 *
 * What it replaced was an adventure-map collage: a 4x NEAREST unit sprite, a
 * rank of tree and cart map objects at three magnifications, a dithered CSS
 * sky and a typeset anti-aliased title over it all. A blind critic named the
 * three pixel densities on one screen before reading a word. the classic's title
 * screen is one painting, one metallic logo and painted buttons, and that is
 * what this is now.
 *
 * The painting is NOT pixel art, so it is the one image in the kit that is
 * drawn at a fractional scale with smooth resampling (`PaintedFrame`). The
 * labels are the serif bitmap face at a whole magnification, like every other
 * caption in the game.
 *
 * Everything the player reads sits inside the centre 4:3 of the frame: the
 * logo starts 13% in from the left and the column ends 13% short of the right
 * edge, so a 4:3 crop of a 16:9 shot cuts paint and not words.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { useAtlas } from "../hooks/useAtlas.js";
import { BitmapText, useBitmapScale } from "../theme/bitmapText.js";
import { townBackgroundKey } from "./art.js";
import { usePolishStyles } from "./polish.js";
import { ScaledSprite, ScreenStyles, useMeasure } from "./primitives.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/**
 * What a plate on the title screen asks for.
 *
 * `kingdom` used to be a member no item ever carried and no host ever handled;
 * it is gone, because a union member with no plate is the same defect as a
 * plate with no handler, read from the other end. `resume` and `settings` are
 * here because `app.go({kind:"menu"})` does not tear the session down - a
 * player who steps out of a game must be able to step back into it, and the
 * only settings the app had were reachable from `debug.ts` alone.
 */
export type MainMenuAction =
  | "new" | "campaign" | "load" | "resume" | "scores" | "editor" | "settings"
  | "credits" | "quit";

export interface MainMenuItem {
  readonly action: MainMenuAction;
  readonly label: string;
  readonly enabled?: boolean;
  readonly hint?: string;
}

export interface MainMenuProps {
  readonly title?: string;
  readonly subtitle?: string;
  /** Which faction's painting stands behind the panel screens; the title
   *  screen itself has its own key art and ignores it. */
  readonly backdropFaction?: string;
  readonly items?: readonly MainMenuItem[];
  readonly version?: string;
  readonly onSelect: (action: MainMenuAction) => void;
}

const DEFAULT_ITEMS: readonly MainMenuItem[] = Object.freeze([
  { action: "new", label: "New Game" },
  { action: "campaign", label: "Campaign" },
  { action: "load", label: "Load Game" },
  { action: "scores", label: "High Scores" },
  { action: "editor", label: "Map Editor" },
  { action: "settings", label: "Settings" },
  { action: "credits", label: "Credits" },
  { action: "quit", label: "Quit" },
]);

/** The two painted frames this screen is made of. Exported so the test can
 *  check both against the shipped atlas manifest. */
export const MENU_KEY_ART = "ui.menu.title_key_art";
export const MENU_LOGO = "ui.menu.title_logo";

// ------------------------------------------------------------ the entries

/**
 * Which entries get the title screen's own treatment, and which do not.
 *
 * The classic editions and their expansions all
 * show FIVE plates, and the three this build has that they do not are the three
 * they deliberately put elsewhere: campaign choice lives inside New Game, the
 * map editor is a separate executable, and settings are not on the title screen
 * at all. So the split is by what the entry IS: the five that start, resume or
 * end a game get a plaque; the three that open a tool or a preference get a
 * small rail beneath them. Nothing is removed - `App.tsx` still hands over all
 * eight and every one is still one click away.
 */
const MINOR_ACTIONS: ReadonlySet<string> = new Set(["editor", "settings", "credits"]);

export const isMinorMenuAction = (action: MainMenuAction): boolean => MINOR_ACTIONS.has(action);

/** One drawn element of an entry's plaque: an atlas frame, and where it sits
 *  relative to the device's centre. Two pieces make a device (a sword over a
 *  shield); one makes an emblem. */
export interface BadgePiece {
  readonly key: string;
  readonly dx: number;
  readonly dy: number;
  readonly turn: number;
}

/**
 * The painted device on each plaque.
 *
 * Nothing new is drawn for this. The atlas already carries painted artifact
 * icons at 128x128 - a crystal globe, a laurel medal, a lion shield, a skull
 * helm - which are the same kind of object the reference paints behind its
 * words and are already in the game's own hand.
 */
export const MENU_ART: Readonly<Partial<Record<MainMenuAction, readonly BadgePiece[]>>> = Object.freeze({
  new: Object.freeze([
    { key: "artifact.brave_heart_brooch", dx: 0, dy: 6, turn: 0 },
    { key: "artifact.colossus_blade", dx: 2, dy: -10, turn: 10 },
  ]),
  campaign: Object.freeze([{ key: "artifact.seers_deck", dx: 0, dy: 0, turn: 0 }]),
  load: Object.freeze([{ key: "artifact.skyvault_orb", dx: 0, dy: 0, turn: 0 }]),
  resume: Object.freeze([{ key: "artifact.roadworn_boots", dx: 0, dy: 0, turn: 0 }]),
  scores: Object.freeze([{ key: "artifact.chancellors_medal", dx: 0, dy: 0, turn: 0 }]),
  quit: Object.freeze([{ key: "artifact.bone_helm", dx: 0, dy: 0, turn: 0 }]),
  editor: Object.freeze([{ key: "artifact.farglass", dx: 0, dy: 0, turn: 0 }]),
  settings: Object.freeze([{ key: "artifact.scrying_lens", dx: 0, dy: 0, turn: 0 }]),
  credits: Object.freeze([{ key: "artifact.spell_book", dx: 0, dy: 0, turn: 0 }]),
});

/** The device at the head of a plaque. Absolutely positioned on the plate's
 *  left end, so it adds nothing to the plaque's own box. */
function MenuBadge(props: { readonly action: MainMenuAction }): JSX.Element | null {
  const pieces = MENU_ART[props.action];
  if (pieces === undefined) return null;
  return (
    <span className="hm-mm__badge" aria-hidden="true">
      {pieces.map((p) => (
        <ScaledSprite
          key={p.key}
          atlasKey={p.key}
          scale={1}
          className="hm-mm__badgepc"
          style={{
            transform:
              `translate(-50%, -50%) translate(${String(p.dx)}px, ${String(p.dy)}px)`
              + ` rotate(${String(p.turn)}deg)`,
          }}
          title=""
        />
      ))}
    </span>
  );
}

// ------------------------------------------------------- painted frames

/**
 * An atlas frame drawn at a FRACTIONAL scale with smooth resampling.
 *
 * Every other sprite in the kit is pixel art and is drawn at a whole
 * magnification through `ScaledSprite`; the title painting and the logo are
 * the two frames that are not, so they take the size the layout asks for.
 * `cover` fills the box the way `object-fit: cover` would; otherwise `width`
 * sets the drawn width and the height follows the frame's own ratio.
 */
function PaintedFrame(props: {
  readonly atlasKey: string;
  readonly width?: number;
  readonly cover?: { readonly w: number; readonly h: number };
  readonly className?: string;
  readonly style?: CSSProperties;
}): JSX.Element {
  const atlas = useAtlas();
  const frame = atlas.frame(props.atlasKey);
  const style = useMemo<CSSProperties>(() => {
    if (frame === null) return { ...props.style };
    const sheet = atlas.manifest.sheets.find((s) => s.id === frame.sheet);
    const k = props.cover !== undefined
      ? Math.max(props.cover.w / frame.w, props.cover.h / frame.h)
      : (props.width ?? frame.w) / frame.w;
    const w = frame.w * k;
    const h = frame.h * k;
    const out: CSSProperties = {
      width: `${String(w)}px`,
      height: `${String(h)}px`,
      backgroundPosition: `${String(-frame.x * k)}px ${String(-frame.y * k)}px`,
      backgroundRepeat: "no-repeat",
      imageRendering: "auto",
    };
    if (sheet !== undefined) {
      out.backgroundImage = `url(${atlas.sheetUrl(sheet.id)})`;
      out.backgroundSize = `${String(sheet.w * k)}px ${String(sheet.h * k)}px`;
    }
    if (props.cover !== undefined) {
      out.position = "absolute";
      out.left = `${String((props.cover.w - w) / 2)}px`;
      out.top = `${String((props.cover.h - h) / 2)}px`;
    }
    return { ...out, ...props.style };
  }, [atlas, frame, props.cover, props.width, props.style]);
  return (
    <span
      className={`${props.className ?? ""}${frame === null ? " hm-icon hm-icon--missing" : ""}`}
      style={style}
      role="presentation"
    />
  );
}

// ------------------------------------------------------------- the screen

export function MainMenu(props: MainMenuProps): JSX.Element {
  usePolishStyles();
  useMenuStyles();
  const [ref, box] = useMeasure<HTMLDivElement>();
  const items = props.items ?? DEFAULT_ITEMS;
  /* The rail's small entries step 2 -> 1 on a laptop window, so nothing is
     ever set on a half pixel. The five big labels are NOT bitmap: see
     \`.hm-mm__gold\` - the reference's title-screen lettering is smooth
     bevelled gold, and a 3x bitmap stair-stepped against it. */
  const smallScale = useBitmapScale(2);

  /* The cursor starts as null so a still of the title screen has no plate lit:
     a highlight nobody asked for reads as a stuck hover. `onSelect` is kept out
     of the effect's dependencies through a ref - rebinding a window listener on
     every render of the host is how the menu ended up swallowing the first
     keypress after a re-render. */
  const [cursor, setCursor] = useState<number | null>(null);

  const tiers = useMemo(() => {
    const major: { readonly item: MainMenuItem; readonly at: number }[] = [];
    const minor: { readonly item: MainMenuItem; readonly at: number }[] = [];
    items.forEach((item, at) => {
      (isMinorMenuAction(item.action) ? minor : major).push({ item, at });
    });
    return { major, minor };
  }, [items]);

  const enabled = useMemo(
    () => [...tiers.major, ...tiers.minor]
      .filter((e) => e.item.enabled !== false)
      .map((e) => e.at),
    [tiers],
  );
  const live = useRef({ items, enabled, onSelect: props.onSelect });
  live.current = { items, enabled, onSelect: props.onSelect };

  const step = useCallback((delta: number): void => {
    setCursor((was) => {
      const ring = live.current.enabled;
      if (ring.length === 0) return null;
      if (was === null) return delta > 0 ? (ring[0] ?? null) : (ring[ring.length - 1] ?? null);
      const at = ring.indexOf(was);
      const next = at < 0 ? 0 : (at + delta + ring.length) % ring.length;
      return ring[next] ?? null;
    });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      // The host lays Settings, Load and Credits over this screen rather than
      // unmounting it, so the listener asks whether the menu is what is on top
      // at the middle of the frame before it takes an arrow key.
      const root = ref.current;
      if (root === null) return;
      const onTop = document.elementFromPoint(
        Math.floor(window.innerWidth / 2),
        Math.floor(window.innerHeight / 2),
      );
      if (onTop !== null && !root.contains(onTop)) return;
      const t = e.target;
      if (t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowDown") { e.preventDefault(); step(1); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); step(-1); return; }
      if (e.key === "Enter" || e.key === " ") {
        if (t instanceof HTMLElement && t.tagName === "BUTTON") return;
        setCursor((was) => {
          if (was === null) return was;
          const item = live.current.items[was];
          if (item !== undefined && item.enabled !== false) live.current.onSelect(item.action);
          return was;
        });
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [step, ref]);

  const stamp = (props.version ?? "").trim();
  const showStamp = /^v?\d/.test(stamp);
  const cover = box.w > 0 && box.h > 0 ? { w: box.w, h: box.h } : { w: 1920, h: 1080 };
  /* The logo is 855 source pixels wide, 44.5% of a 1920 frame; it keeps that
     share of the width on a narrower window rather than a fixed pixel size. */
  const logoWidth = Math.round(Math.min(855, cover.w * 0.445));

  return (
    <div className="hs-screen" ref={ref} role="region" aria-label="Main menu">
      <ScreenStyles />

      <div className="hs-menu hm-mm">
        <div className="hm-mm__paint" aria-hidden="true">
          <PaintedFrame atlasKey={MENU_KEY_ART} cover={cover} className="hm-mm__key" />
        </div>
        {/* A quiet fall-off behind the column and under the rail, so the
            plaques and the foot read against paint that is not fighting them. */}
        <div className="hm-mm__shade" aria-hidden="true" />

        <div className="hm-mm__logo">
          <PaintedFrame atlasKey={MENU_LOGO} width={logoWidth} className="hm-mm__logoart" />
          <span className="hs-sr">{props.title ?? "Banners of the Realm"}</span>
          <div className="hm-mm__sub">
            <BitmapText face="serif" shadow scale={smallScale}>
              {(props.subtitle ?? "A turn-based strategy game").toUpperCase()}
            </BitmapText>
          </div>
        </div>

        <nav
          className={"hm-mm__column" + (tiers.major.length > 5 ? " hm-mm__column--long" : "")}
          aria-label="Main menu"
        >
          {tiers.major.map(({ item, at }) => (
            <button
              key={item.action}
              type="button"
              className={"hm-mm__plaque" + (at === cursor ? " hm-mm__plaque--at" : "")}
              disabled={item.enabled === false}
              title={item.hint ?? item.label}
              onMouseEnter={() => { setCursor(at); }}
              onMouseLeave={() => { setCursor((was) => (was === at ? null : was)); }}
              onClick={() => props.onSelect(item.action)}
            >
              <MenuBadge action={item.action} />
              <span className="hm-mm__label hm-mm__gold">
                {item.label.toUpperCase()}
              </span>
            </button>
          ))}

          {tiers.minor.length === 0 ? null : (
            <div className="hm-mm__minor">
              {tiers.minor.map(({ item, at }) => (
                <button
                  key={item.action}
                  type="button"
                  className={"hm-mm__small" + (at === cursor ? " hm-mm__small--at" : "")}
                  disabled={item.enabled === false}
                  title={item.hint ?? item.label}
                  onMouseEnter={() => { setCursor(at); }}
                  onMouseLeave={() => { setCursor((was) => (was === at ? null : was)); }}
                  onClick={() => props.onSelect(item.action)}
                >
                  <BitmapText face="serif" shadow scale={smallScale}>
                    {item.label.toUpperCase()}
                  </BitmapText>
                </button>
              ))}
            </div>
          )}
        </nav>

        {/* The reference's foot: a line or two of small type in the bottom-left
            corner and nothing else - no key prompts, no rail. The seal and the
            version stand there in the small serif. */}
        <div className="hm-mm__foot" aria-hidden="true">
          <span className="hm-mm__seal">
            <BitmapText face="serif" shadow scale={1}>THE REALM · YEAR OF THE SKYCLAW</BitmapText>
          </span>
          {showStamp ? (
            <span className="hm-mm__stampinline">
              <BitmapText face="serif" shadow scale={1}>{stamp}</BitmapText>
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- the backdrop

/**
 * The painting the panel screens stand on.
 *
 * New Game, Settings and Load Game shipped over `hs-screen`'s flat stone
 * gradient, and at 1920x1080 that is most of the frame. The town painting at a
 * whole magnification, pushed well down so the 11px text of the panels on top
 * of it stays the thing a reader looks at.
 */
export function ScreenBackdrop(props: { readonly faction?: string }): JSX.Element {
  useMenuStyles();
  const [ref, box] = useMeasure<HTMLDivElement>();
  const cover = useMemo(() => {
    if (box.w === 0 || box.h === 0) return 2;
    return Math.max(1, Math.ceil(Math.max(box.w / 1280, box.h / 720)));
  }, [box.w, box.h]);
  return (
    <div className="hm-bd" ref={ref} aria-hidden="true">
      <ScaledSprite
        atlasKey={townBackgroundKey(props.faction ?? "sunspire")}
        scale={cover}
        className="hm-bd__art"
        title=""
      />
      <span className="hm-bd__veil" />
    </div>
  );
}

// ----------------------------------------------------------------- credits

/** What the Credits plate opens.
 *
 * It lives beside the menu rather than in a file of its own because it is the
 * title screen's second page: the plate and the screen it opens are one
 * feature, and splitting them is how the plate ended up wired to nothing.
 *
 * Every number below is countable in the repository. A credits roll that
 * invents a studio is worse than none, so this names the parts, the art
 * library it draws from and the game it is modelled on, and stops.
 */
export interface CreditsScreenProps {
  readonly version?: string | undefined;
  readonly onClose: () => void;
}

interface CreditsSection {
  readonly title: string;
  readonly lines: readonly string[];
}

export const CREDITS: readonly CreditsSection[] = Object.freeze([
  {
    title: "The Engine",
    lines: Object.freeze([
      "core - the rules kernel: deterministic, integer-only, and the sole authority "
      + "on every outcome. It imports nothing from a browser.",
      "content - the creatures, heroes, spells, artifacts, buildings and the map generator.",
      "ai - the computer players, which issue their commands down the same pipe you do.",
      "render - the world and battle presenters, drawn with Pixi.",
      "ui - the screens you are reading this on.",
    ]),
  },
  {
    title: "The Art",
    lines: Object.freeze([
      "12 faction unit sheets and 12 painted town screens.",
      "345 map-object designs over the terrain atlases.",
      "199 hero portraits, 69 spells, 22 artifacts, 69 interface icons.",
      "19 battle arenas and 5 war machines.",
    ]),
  },
  {
    title: "Standing On",
    lines: Object.freeze([
      "The turn-based fantasy strategy classics of the late 1990s. This is an independent "
      + "engine with original content, not affiliated with or derived from any of them.",
      "React, Pixi, Vite, TypeScript, Vitest, Playwright.",
    ]),
  },
]);

export function CreditsScreen(props: CreditsScreenProps): JSX.Element {
  usePolishStyles();
  const stamp = (props.version ?? "").trim();

  return (
    <div className="hs-screen" role="region" aria-label="Credits">
      <ScreenStyles />

      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">Credits</span>
        {stamp === "" ? null : <span className="hs-screen__sub hs-muted">{stamp}</span>}
        <span className="hs-spacer" />
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-screen__body" style={{ justifyContent: "center", padding: "14px" }}>
        <div
          className="hs-scroll"
          style={{ width: "min(720px, 96%)", display: "flex", flexDirection: "column", gap: "10px" }}
        >
          {CREDITS.map((section) => (
            <div key={section.title} className="hs-card hs-wood">
              <div className="hs-card__title">{section.title}</div>
              {section.lines.map((line) => (
                <div key={line} className="hs-tiny hs-muted" style={{ lineHeight: 1.55, marginBottom: "4px" }}>
                  {line}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const MENU_STYLE_ID = "hm-mainmenu-art";

/**
 * The title screen's own sheet. It is mounted after `polish.ts`, so a rule here
 * on a class that sheet also styles is the one that survives.
 *
 * The plaque geometry is stated in pixels and repeated for the laptop window
 * in the media block at the foot; `test/main-menu.spec.ts` reads both back.
 */
export const MENU_CSS = `
.hs-menu.hm-mm {
  --mm-rail: 58px;
  --mm-plaque-h: 92px;
  --mm-plaque-w: 520px;
  --mm-pitch: 138px;
  --mm-label: 44px;
  --mm-badge: 1;
  display: block;
  overflow: hidden;
  background-color: #090b12;
}
/* The collage's layers, all retired: no sky ramp, no veil, no rays. */
.hs-menu.hm-mm::before, .hs-menu.hm-mm::after { display: none; }
.hs-menu__crest::before { display: none; }

/* ---- the painting */
.hm-mm__paint { position: absolute; inset: 0; z-index: 0; overflow: hidden; }
.hm-mm__key { display: block; }
/* Two washes and nothing else: a pool the plaques sit in on the right, and a
   foot the rail is bedded into. Both are soft on purpose - this is a painting,
   not the dithered pixel art the old sheet spent its rules protecting. */
.hm-mm__shade {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    radial-gradient(46% 70% at 79% 52%, rgba(4,6,12,0.46) 0%, rgba(4,6,12,0.22) 55%, rgba(4,6,12,0) 100%),
    linear-gradient(180deg, rgba(0,0,0,0) 0 82%, rgba(3,4,8,0.55) 100%);
}

/* ---- the logo, upper left, inside the 4:3-safe band */
.hm-mm__logo {
  position: absolute;
  left: 13%;
  top: 4.2vh;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  pointer-events: none;
}
.hm-mm__logoart { display: block; filter: drop-shadow(0 10px 22px rgba(0,0,0,0.75)); }
.hm-mm__sub { display: flex; color: #f0d68c; line-height: 0; letter-spacing: 0; }
.hm-mm__sub svg { display: block; }

/* ---- the column of plaques, hung inside the right 4:3-safe edge */
.hm-mm__column {
  position: absolute;
  right: 13%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: calc(var(--mm-pitch) - var(--mm-plaque-h));
  width: var(--mm-plaque-w);
}
.hm-mm__column--long { --mm-pitch: 118px; }

/* NO PLATE. the classic's title buttons are a painted device with the label
   set beside it in smooth, bevelled gold serif with a dark outline - there is
   no plaque, no bezel and no rivet (REF_expansion-menu_button_3x). The round-1
   bronze plates with 3x bitmap labels were the first tell in every blind
   round. The button is the device and the lettering, lit on hover and dropped
   a pixel when pressed, the way the reference swaps in its pressed frame. */
.hm-mm__plaque {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: var(--mm-plaque-h);
  padding: 0 0 0 126px;
  border: 0;
  cursor: pointer;
  background: none;
  text-align: left;
  transition: filter 80ms linear, transform 80ms linear;
}
.hm-mm__label { display: block; }
/* Smooth bevelled gold: a vertical gilt ramp clipped to the glyphs, a 1px
   dark outline and a 2px seat under it from the drop-shadow chain. Serif,
   bold, and NOT a bitmap face - the reference's lettering is anti-aliased
   at every size it ships at. */
.hm-mm__gold {
  font-family: "C059", "Nimbus Roman", "Liberation Serif", "Times New Roman", Georgia, serif;
  font-weight: 700;
  font-size: var(--mm-label);
  line-height: 1;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: #ffe9b6;
  background-image: linear-gradient(178deg, #fffdf2 0%, #ffeeb8 30%, #e8c163 52%, #b8842a 70%, #fff0c4 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter:
    drop-shadow(1px 0 0 #1a1004)
    drop-shadow(-1px 0 0 #1a1004)
    drop-shadow(0 1px 0 #1a1004)
    drop-shadow(0 -1px 0 #1a1004)
    drop-shadow(0 2px 0 #4a3210)
    drop-shadow(0 4px 6px rgba(0,0,0,0.85));
}

/* The painted device, seated on the plate's left end and standing a little
   proud of it. */
.hm-mm__badge {
  position: absolute;
  left: 58px;
  top: 50%;
  width: 0;
  height: 0;
  z-index: 1;
  pointer-events: none;
  transform: scale(var(--mm-badge));
}
/* Smooth, not pixelated: these are painted 128px icons with soft shading in
   them, drawn at 0.86, and the kit's \`hm-icon--up\` would snap them to the
   grid. The longer selector outranks it. */
.hm-mm__badge .hm-icon.hm-mm__badgepc {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  image-rendering: auto;
  filter: drop-shadow(0 4px 6px rgba(0,0,0,0.8));
}

.hm-mm__plaque:hover:not(:disabled),
.hm-mm__plaque--at:not(:disabled) {
  filter: brightness(1.16);
}
.hm-mm__plaque:hover:not(:disabled) .hm-mm__gold,
.hm-mm__plaque--at:not(:disabled) .hm-mm__gold {
  background-image: linear-gradient(178deg, #ffffff 0%, #fff6d2 30%, #f4d27a 52%, #c9933a 70%, #fff6d8 100%);
}
.hm-mm__plaque:active:not(:disabled) {
  transform: translateY(2px);
  filter: brightness(0.9);
}
.hm-mm__plaque:focus { outline: none; }
.hm-mm__plaque:focus-visible { outline: 2px solid #ffe6ac; outline-offset: 3px; }
.hm-mm__plaque:disabled { cursor: not-allowed; filter: saturate(0.35) brightness(0.72); }
.hm-mm__plaque:disabled .hm-mm__badgepc { filter: grayscale(0.85) brightness(0.5); }

/* ---- the rail of small entries under the column */
.hm-mm__minor {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px 22px;
  margin-top: 4px;
  padding-top: 14px;
}
.hm-mm__minor::before {
  content: "";
  position: absolute;
  left: 6%;
  right: 6%;
  top: 0;
  height: 1px;
  background-image: linear-gradient(90deg,
    rgba(216,178,96,0) 0%, rgba(216,178,96,0.55) 22%,
    rgba(216,178,96,0.55) 78%, rgba(216,178,96,0) 100%);
}
.hm-mm__small {
  display: inline-flex;
  align-items: center;
  padding: 4px 2px;
  border: 0;
  background: none;
  cursor: pointer;
  color: #d3b578;
  line-height: 0;
  transition: color 90ms linear;
}
.hm-mm__small svg { display: block; }
.hm-mm__small:hover:not(:disabled),
.hm-mm__small--at:not(:disabled) { color: #ffe6ac; }
.hm-mm__small:disabled { color: #7c7565; cursor: not-allowed; }
.hm-mm__small:focus { outline: none; }
.hm-mm__small:focus-visible { outline: 2px solid #ffe6ac; outline-offset: 4px; }

/* ---- the foot: small type in the bottom-left corner, the reference's own.
   The rail of key prompts that used to sit here was a console idiom. */
.hm-mm__foot {
  position: absolute;
  left: 22px; bottom: 18px;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  pointer-events: none;
}
.hm-mm__seal, .hm-mm__stampinline { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 10px; color: #b39a66; line-height: 0; }
.hm-mm__seal svg, .hm-mm__stampinline svg { display: block; }
.hm-mm__stampinline { color: #8a7a5d; }

/* ---- the panel screens' chrome.
   Opt-in by class: this sheet is mounted for as long as any screen with a
   backdrop is up, and a rule on the kit's own class would restyle every bar. */
.hs-screen__title.hm-cap {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: 30px;
  line-height: 1.1;
  letter-spacing: 0.06em;
  color: #ffe9b6;
  background-image: linear-gradient(178deg, #fffdf2 0%, #ffeeb8 34%, #e0b455 68%, #fff2cc 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter:
    drop-shadow(1px 0 0 #120c03)
    drop-shadow(-1px 0 0 #120c03)
    drop-shadow(0 1px 0 #120c03)
    drop-shadow(0 -1px 0 #120c03)
    drop-shadow(0 2px 0 #4a3210)
    drop-shadow(0 3px 5px rgba(0,0,0,0.9));
}

/* ---- the panel screens' backdrop */
.hm-bd { position: absolute; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
.hm-bd__art {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  image-rendering: pixelated;
  filter: brightness(0.46) saturate(0.62) contrast(1.03);
}
.hm-bd__veil {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(92% 100% at 50% 44%,
      rgba(6,8,12,0) 0%, rgba(6,8,12,0.24) 62%, rgba(4,5,9,0.56) 100%),
    linear-gradient(180deg,
      rgba(6,8,12,0.58) 0 5%,
      rgba(6,8,12,0.26) 5% 20%,
      rgba(6,8,12,0.16) 20% 62%,
      rgba(5,6,10,0.24) 62% 84%,
      rgba(4,5,9,0.36) 84% 100%);
}

@media (max-width: 1500px) {
  .hs-screen__title.hm-cap { font-size: 24px; }
  /* 768 rows against 1080: the same column at 0.72 of the pitch, the label
     one magnification down (cap 22), the device at two thirds. */
  .hs-menu.hm-mm {
    --mm-rail: 46px;
    --mm-plaque-h: 66px;
    --mm-plaque-w: 360px;
    --mm-pitch: 98px;
    --mm-badge: 0.62;
    --mm-label: 32px;
  }
  .hm-mm__column--long { --mm-pitch: 84px; }
  .hm-mm__plaque { padding: 0 0 0 88px; }
  .hm-mm__badge { left: 40px; }
  .hm-mm__logo { top: 3.5vh; gap: 7px; }
  .hm-mm__minor { gap: 4px 16px; padding-top: 10px; }
}

.hm-root--phone .hs-menu.hm-mm {
  --mm-rail: 36px;
  --mm-plaque-h: 44px;
  --mm-plaque-w: min(320px, 72vw);
  --mm-pitch: 50px;
  --mm-badge: 0.48;
}
.hm-root--phone .hm-mm__gold { font-size: max(22px, var(--hm-type-floor)); }
.hm-root--phone .hm-mm__column {
  top: auto;
  bottom: 8%;
  transform: none;
  max-height: 72%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.hm-root--phone .hm-mm__column--long { --mm-pitch: 46px; }
.hm-root--phone .hm-mm__plaque { padding: 0 0 0 56px; min-height: 44px; }
.hm-root--phone .hm-mm__badge { left: 8px; }
.hm-root--phone .hm-mm__logo { top: 2vh; gap: 4px; }
`;

/** Mounts this screen's sheet once, and always after `polish.ts` (the `screen` layer). */
function useMenuStyles(): void {
  useStyleSheet(MENU_STYLE_ID, MENU_CSS, StyleLayer.screen);
}
